/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { ISchemaConstStringModel, isSchemaConstStringModel } from "@adapptio/lib-blueprint";
import { INSPECTOR_NODE_TYPE, TInspectorNode } from "../InspectorNodeTypes";
import { INSPECTOR_CONTROL_TYPE, TInspectorControl } from "../controls";
import { ICodeEditorProps } from "@adapptio/lib-editor-ui";
import { mapperDescriptor } from "../../modules/Core/ReferenceGuide/mappers/mapperDescriptor";
import { mapperExamples } from "../../modules/Core/ReferenceGuide/mappers/mapperExamples";
import { getParentComponentName } from "../utils";

const codeControlTypeLanguages: Record<string, ICodeEditorProps["language"]> = {
	codeJavaScript: "javascript",
	codeJson: "json",
	codeYaml: "yaml",
	codeXML: "xml",
	codeHTML: "html",
	codeCSS: "css",
	codeSQL: "sql",
	codeCsv: "csv"
};

function getConstStringControl(model: ISchemaConstStringModel): TInspectorControl {
	const controlType = model.schema?.opts.editorOptions?.controlType;
	const controlOptions = model.schema?.opts.editorOptions?.controlOptions || {};
	const key = String(model.nodeId);

	if (controlType === "actionSelector") {
		return {
			key,
			type: INSPECTOR_CONTROL_TYPE.ACTION_SELECTOR,
			model
		};
	}

	if (controlType === "viewSelector") {
		return {
			key,
			type: INSPECTOR_CONTROL_TYPE.VIEW_SELECTOR,
			model
		};
	}

	if (controlType === "routeSelector") {
		return {
			key,
			type: INSPECTOR_CONTROL_TYPE.ROUTE_SELECTOR,
			model
		};
	}

	if (controlType === "iconSelector") {
		return {
			key,
			type: INSPECTOR_CONTROL_TYPE.ICON,
			model
		};
	}

	if (controlType === "themeSelector") {
		return {
			key,
			type: INSPECTOR_CONTROL_TYPE.THEME_SELECTOR,
			model
		};
	}

	if (controlType === "textarea") {
		return {
			key,
			type: INSPECTOR_CONTROL_TYPE.STRING,
			model,
			multiline: true
		};
	}

	if (codeControlTypeLanguages[controlType]) {
		return {
			key,
			type: INSPECTOR_CONTROL_TYPE.CODE,
			model,
			language: codeControlTypeLanguages[controlType],
			options: controlOptions
		};
	}

	if (model.schema.opts.allowInterpolation) {
		return {
			key,
			type: INSPECTOR_CONTROL_TYPE.CODE,
			model,
			language: model.schema.opts.format ?? "adapptioExpressionInterpolation"
		};
	}

	return {
		key,
		type: INSPECTOR_CONTROL_TYPE.CODE,
		model,
		language: model.schema.opts.format ?? undefined
	};
}

export function constStringMapper(model: ISchemaConstStringModel): TInspectorNode {
	if (!isSchemaConstStringModel(model)) {
		return;
	}

	const control = getConstStringControl(model);

	return {
		type: INSPECTOR_NODE_TYPE.DEFAULT,
		key: `${model.nodeId}`,
		label: model.schema.opts.label,
		tooltip: model.schema.opts.description,
		default: model.schema.opts.default || null,
		options: model.schema.opts["options"],
		required: model.schema.opts.constraints?.required || false,
		descriptor: mapperDescriptor(model.schema),
		referenceData: getParentComponentName(model),
		examples: mapperExamples(model.schema),
		deps: [ model ],
		controls: [ control ],
		children: []
	};
}
